import { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getAllCategoriesWithProductCountUrl,
  allProductUrl,
  updateCategorySortOrderUrl,
  updateProductSortOrderUrl,
} from "../../../utils/urls";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function DragHandle() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="text-ink-muted shrink-0 cursor-grab active:cursor-grabbing"
    >
      <circle cx="5.5" cy="4" r="1.2" fill="currentColor" />
      <circle cx="10.5" cy="4" r="1.2" fill="currentColor" />
      <circle cx="5.5" cy="8" r="1.2" fill="currentColor" />
      <circle cx="10.5" cy="8" r="1.2" fill="currentColor" />
      <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="10.5" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

function SortableCategoryItem({ item, isSelected, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.category_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer select-none transition-colors ${
        isSelected
          ? "bg-primary-light border border-primary/20"
          : "bg-white border border-gray-100 hover:border-gray-200 hover:bg-gray-50"
      }`}
    >
      <span {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
        <DragHandle />
      </span>
      {item.logo_url ? (
        <img src={item.logo_url} alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-md bg-primary-light flex items-center justify-center shrink-0">
          <span className="text-primary text-xs font-bold">
            {item.category_title?.charAt(0)?.toUpperCase()}
          </span>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium truncate ${isSelected ? "text-primary" : "text-ink"}`}>
          {item.category_title}
        </p>
        <p className="text-xs text-ink-muted">
          {item.product_count ?? 0} products · {item.sub_category_count ?? 0} subcategories
        </p>
      </div>
    </div>
  );
}

function SortableProductItem({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.product_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-gray-100 hover:border-gray-200 select-none"
    >
      <span {...attributes} {...listeners}>
        <DragHandle />
      </span>
      {item.logo_url ? (
        <img src={item.logo_url} alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-ink-muted">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink truncate">{item.product_name}</p>
        {item.company_name && (
          <p className="text-xs text-ink-muted truncate">{item.company_name}</p>
        )}
      </div>
    </div>
  );
}

function SaveButton({ onClick, saving, dirty }) {
  return (
    <button
      onClick={onClick}
      disabled={saving || !dirty}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
        dirty
          ? "bg-primary text-white hover:bg-primary-dark"
          : "bg-gray-100 text-ink-muted cursor-not-allowed"
      }`}
    >
      {saving ? (
        <>
          <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"/>
          </svg>
          Saving…
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Save order
        </>
      )}
    </button>
  );
}

export default function ProductOrdering() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);
  const [catDirty, setCatDirty] = useState(false);
  const [prodDirty, setProdDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    axios
      .get(getAllCategoriesWithProductCountUrl + "?is_all=true", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (res.data.success) {
          const items = res.data.data ?? [];
          const sorted = [...items].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
          setCategories(sorted);
        }
      })
      .catch(() => toast.error("Failed to load categories"))
      .finally(() => setLoadingCategories(false));
  }, []);

  const loadProducts = useCallback((categoryId) => {
    setLoadingProducts(true);
    setProdDirty(false);
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    axios
      .get(`${allProductUrl}?category_id=${categoryId}&is_all=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (res.data.success) {
          const items = res.data.data ?? [];
          const sorted = [...items].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
          setProducts(sorted);
        }
      })
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoadingProducts(false));
  }, []);

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    loadProducts(cat.category_id);
  };

  const handleCatDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setCategories((prev) => {
      const oldIdx = prev.findIndex((c) => c.category_id === active.id);
      const newIdx = prev.findIndex((c) => c.category_id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
    setCatDirty(true);
  };

  const handleProdDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setProducts((prev) => {
      const oldIdx = prev.findIndex((p) => p.product_id === active.id);
      const newIdx = prev.findIndex((p) => p.product_id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
    setProdDirty(true);
  };

  const saveCategories = async () => {
    setSavingCategories(true);
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const items = categories.map((c, i) => ({ category_id: c.category_id, sort_order: i }));
    try {
      await axios.put(
        updateCategorySortOrderUrl,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Category order saved");
      setCatDirty(false);
    } catch {
      toast.error("Failed to save category order");
    } finally {
      setSavingCategories(false);
    }
  };

  const saveProducts = async () => {
    setSavingProducts(true);
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const items = products.map((p, i) => ({ product_id: p.product_id, sort_order: i }));
    try {
      await axios.put(
        updateProductSortOrderUrl,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Product order saved");
      setProdDirty(false);
    } catch {
      toast.error("Failed to save product order");
    } finally {
      setSavingProducts(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-1">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#0F172A]">Product Ordering</h1>
        <p className="text-sm text-[#64748B] mt-0.5">
          Drag to reorder categories and products shown in the mobile app
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Categories panel */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-semibold text-ink">Categories</p>
              <p className="text-xs text-ink-muted mt-0.5">{categories.length} total</p>
            </div>
            <SaveButton onClick={saveCategories} saving={savingCategories} dirty={catDirty} />
          </div>

          <div className="flex-1 overflow-y-auto p-3 max-h-[70vh]">
            {loadingCategories ? (
              <div className="flex items-center justify-center h-32 text-ink-muted text-sm">
                Loading…
              </div>
            ) : categories.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-ink-muted text-sm">
                No categories found
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleCatDragEnd}
              >
                <SortableContext
                  items={categories.map((c) => c.category_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-1.5">
                    {categories.map((cat) => (
                      <SortableCategoryItem
                        key={cat.category_id}
                        item={cat}
                        isSelected={selectedCategory?.category_id === cat.category_id}
                        onClick={() => handleCategorySelect(cat)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        {/* Products panel */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-semibold text-ink">
                {selectedCategory ? selectedCategory.category_title : "Products"}
              </p>
              <p className="text-xs text-ink-muted mt-0.5">
                {selectedCategory
                  ? `${products.length} products — select category to reorder`
                  : "Select a category on the left"}
              </p>
            </div>
            {selectedCategory && (
              <SaveButton onClick={saveProducts} saving={savingProducts} dirty={prodDirty} />
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3 max-h-[70vh]">
            {!selectedCategory ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3 text-ink-muted">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-30">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
                <p className="text-sm">Click a category to reorder its products</p>
              </div>
            ) : loadingProducts ? (
              <div className="flex items-center justify-center h-32 text-ink-muted text-sm">
                Loading…
              </div>
            ) : products.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-ink-muted text-sm">
                No products in this category
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleProdDragEnd}
              >
                <SortableContext
                  items={products.map((p) => p.product_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-1.5">
                    {products.map((prod) => (
                      <SortableProductItem key={prod.product_id} item={prod} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
